<template>
    <section class="ipg" aria-label="Investment Plans">

        <!-- Section header -->
        <div class="ipg-head">
            <div class="ipg-head-l">
                <span class="ipg-kicker" :style="`color:${themeColors.primary};background:rgba(${themeColors.primaryRgb},0.09);border-color:rgba(${themeColors.primaryRgb},0.22);`">
                    <i class="ipg-kicker-dot" :style="`background:${themeColors.primary};box-shadow:0 0 6px ${themeColors.primary};`"></i>
                    {{ t('investmentPlans') }}
                </span>
                <h2 class="ipg-head-title">{{ t('choosePerfectPlan') }}</h2>
            </div>
            <div class="ipg-count" :style="`border-color:rgba(${themeColors.primaryRgb},0.2);`">
                <span class="ipg-count-n" :style="`color:${themeColors.primary};`">{{ activePlans.length }}</span>
                <span class="ipg-count-l">{{ t('available') }}</span>
            </div>
        </div>

        <!-- Cards grid -->
        <div class="ipg-grid">
            <div
                v-for="(plan, index) in plans"
                :key="plan.id"
                :id="`investment-plan-${plan.id}`"
                class="ipc"
                :class="[plan.featured ? 'ipc--featured' : '', plan.status === 'coming_soon' ? 'ipc--soon' : '', plan.id === highlightedPlanId ? 'ipc--highlighted' : '']"
                :style="`animation-delay:${index * 80}ms`"
                role="region"
                :aria-labelledby="`plan-title-${plan.id}`"
                tabindex="0"
            >
                <!-- Left accent stripe -->
                <div class="ipc-stripe"
                     :style="plan.featured
                        ? 'background:linear-gradient(180deg,#f59e0b,#fbbf24,#f59e0b);box-shadow:2px 0 12px rgba(245,158,11,0.4);'
                        : plan.status === 'coming_soon'
                            ? 'background:linear-gradient(180deg,#475569,#64748b);'
                            : `background:linear-gradient(180deg,#7c3aed,${themeColors.primary},${themeColors.primaryLight});box-shadow:2px 0 14px rgba(${themeColors.primaryRgb},0.4);`">
                </div>

                <!-- Corner glow overlay -->
                <div class="ipc-corner-glow"
                     :style="plan.featured
                        ? 'background:radial-gradient(ellipse at 100% 0%,rgba(245,158,11,0.14),transparent 65%);'
                        : `background:radial-gradient(ellipse at 100% 0%,rgba(${themeColors.primaryRgb},0.11),transparent 65%);`">
                </div>

                <!-- Top shimmer line -->
                <div class="ipc-shimline"
                     :style="plan.featured
                        ? 'background:linear-gradient(90deg,transparent,rgba(245,158,11,0.38),transparent);'
                        : `background:linear-gradient(90deg,transparent,rgba(${themeColors.primaryRgb},0.28),transparent);`">
                </div>

                <!-- Badges -->
                <div class="ipc-badges">
                    <span v-if="plan.featured" class="ipc-badge ipc-badge--gold">
                        <svg width="9" height="9" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                        </svg>
                        {{ t('featured') }}
                    </span>
                    <span v-if="plan.status === 'coming_soon'" class="ipc-badge ipc-badge--soon">
                        <svg width="9" height="9" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                            <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                        </svg>
                        {{ t('comingSoon') }}
                    </span>
                </div>

                <!-- Card body -->
                <div class="ipc-body">

                    <!-- Rate hero box -->
                    <div class="ipc-rate-hero"
                         :style="plan.featured
                            ? 'background:rgba(245,158,11,0.07);border-color:rgba(245,158,11,0.18);'
                            : plan.status === 'coming_soon'
                                ? 'background:rgba(71,85,105,0.08);border-color:rgba(71,85,105,0.22);'
                                : `background:rgba(${themeColors.primaryRgb},0.07);border-color:rgba(${themeColors.primaryRgb},0.18);`">
                        <div class="ipc-rate-left">
                            <span class="ipc-rate-type">{{ t(plan.return_type) }} {{ t('returns') }}</span>
                            <span class="ipc-rate"
                                  :style="plan.featured
                                    ? 'background:linear-gradient(135deg,#f59e0b,#fcd34d);-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;'
                                    : plan.status === 'coming_soon'
                                        ? 'color:#64748b;'
                                        : `background:linear-gradient(135deg,${themeColors.primary},${themeColors.primaryLight});-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;`">
                                {{ plan.interest_rate }}%
                            </span>
                        </div>
                        <div class="ipc-rate-icon"
                             :style="plan.featured
                                ? 'background:rgba(245,158,11,0.14);color:#f59e0b;'
                                : `background:rgba(${themeColors.primaryRgb},0.14);color:${themeColors.primary};`">
                            <svg width="20" height="20" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6"/>
                            </svg>
                        </div>
                    </div>

                    <!-- Plan name & desc -->
                    <h3 :id="`plan-title-${plan.id}`" class="ipc-name">{{ plan.name }}</h3>
                    <p class="ipc-desc">{{ plan.description }}</p>

                    <!-- Feature chips -->
                    <div v-if="plan.features && plan.features.length" class="ipc-features">
                        <span
                            v-for="feature in plan.features"
                            :key="feature"
                            class="ipc-chip"
                            :style="`background:rgba(${themeColors.primaryRgb},0.08);border-color:rgba(${themeColors.primaryRgb},0.2);color:${themeColors.primary};`"
                        >{{ feature }}</span>
                    </div>

                    <!-- Divider -->
                    <div class="ipc-divider"
                         :style="plan.featured
                            ? 'background:linear-gradient(90deg,transparent,rgba(245,158,11,0.25),transparent);'
                            : `background:linear-gradient(90deg,transparent,rgba(${themeColors.primaryRgb},0.18),transparent);`">
                    </div>

                    <!-- Key details -->
                    <div class="ipc-details">
                        <div class="ipc-detail">
                            <span class="ipc-detail-ic"
                                  :style="plan.featured ? 'background:rgba(245,158,11,0.1);color:#f59e0b;' : `background:rgba(${themeColors.primaryRgb},0.1);color:${themeColors.primary};`">
                                <svg width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                            </span>
                            <span class="ipc-detail-k">{{ t('range') }}</span>
                            <span class="ipc-detail-v">{{ currencySymbol }}{{ formatCurrency(plan.min_amount) }} – {{ currencySymbol }}{{ formatCurrency(plan.max_amount) }}</span>
                        </div>
                        <div class="ipc-detail">
                            <span class="ipc-detail-ic" style="background:rgba(34,211,238,0.1);color:#22d3ee;">
                                <svg width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                            </span>
                            <span class="ipc-detail-k">{{ t('duration') }}</span>
                            <span class="ipc-detail-v">
                                <span v-if="plan.duration_type === 'lifetime'">{{ t('untilCapReached') || 'Until Cap' }}</span>
                                <span v-else>{{ plan.duration_days }} {{ t('days') }}</span>
                            </span>
                        </div>
                        <div class="ipc-detail">
                            <span class="ipc-detail-ic" style="background:rgba(16,185,129,0.1);color:#10b981;">
                                <svg width="11" height="11" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
                                </svg>
                            </span>
                            <span class="ipc-detail-k">{{ t('principal') }}</span>
                            <span class="ipc-detail-v capitalize">{{ t(plan.principal_return.replace('_','')) }}</span>
                        </div>
                    </div>

                    <!-- Capacity bar hidden -->

                    <!-- CTA Button -->
                    <button
                        @click="$emit('selectPlan', plan)"
                        :disabled="plan.status === 'coming_soon'"
                        class="ipc-btn"
                        :class="plan.status === 'coming_soon' ? 'ipc-btn--soon' : plan.featured ? 'ipc-btn--featured' : 'ipc-btn--active'"
                        :style="plan.status !== 'coming_soon' && !plan.featured
                            ? `background:linear-gradient(135deg,#7c3aed,${themeColors.primary},${themeColors.primaryLight});box-shadow:0 6px 24px rgba(${themeColors.primaryRgb},0.42);`
                            : ''"
                        :aria-label="plan.status === 'coming_soon' ? t('planComingSoon') : `${t('investIn')} ${plan.name}`"
                    >
                        <span class="ipc-btn-shimmer"></span>
                        <span class="ipc-btn-inner">
                            <svg v-if="plan.status === 'coming_soon'" width="14" height="14" fill="none" stroke="currentColor" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
                            </svg>
                            <svg v-else width="14" height="14" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24" aria-hidden="true">
                                <path stroke-linecap="round" stroke-linejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3"/>
                            </svg>
                            {{ plan.status === 'coming_soon' ? t('comingSoon') : t('investNow') }}
                        </span>
                    </button>
                </div>
            </div>
        </div>
    </section>
</template>

<script setup>
import { computed, onMounted } from 'vue'
import { usePage } from '@inertiajs/vue3'
import { useTranslation } from '@/composables/useTranslation'
import { useToast } from '@/composables/useToast'

const props = defineProps({
    plans:             { type: Array,  default: () => [] },
    highlightedPlanId: { type: Number, default: null }
})
const emit = defineEmits(['selectPlan'])

const page = usePage()
const { t } = useTranslation()
const { success, error } = useToast()

const currencySymbol = computed(() => page.props.currencySymbol || '$')
const primaryColor   = computed(() => page.props.primaryColor  || '#a75ded')

const hexToRgb = (hex) => {
    const r = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex)
    return r ? { r: parseInt(r[1],16), g: parseInt(r[2],16), b: parseInt(r[3],16) } : { r:167, g:93, b:237 }
}
const rgbToHex = (r,g,b) => '#'+((1<<24)+(r<<16)+(g<<8)+b).toString(16).slice(1)
const adjustColor = (hex, amt) => {
    const {r,g,b} = hexToRgb(hex)
    return rgbToHex(Math.min(255,Math.max(0,r+amt)), Math.min(255,Math.max(0,g+amt)), Math.min(255,Math.max(0,b+amt)))
}
const themeColors = computed(() => {
    const {r,g,b} = hexToRgb(primaryColor.value)
    return {
        primary:      primaryColor.value,
        primaryDark:  adjustColor(primaryColor.value, -20),
        primaryLight: adjustColor(primaryColor.value, 40),
        primaryRgb:   `${r},${g},${b}`
    }
})

const activePlans = computed(() => props.plans.filter(p => p.status === 'active'))

const formatCurrency = (v) => {
    if (!v || isNaN(v)) return '0'
    const n = parseFloat(v)
    if (n >= 1e6) return (n/1e6).toFixed(1)+'M'
    if (n >= 1e3) return (n/1e3).toFixed(0)+'K'
    return new Intl.NumberFormat('en-US',{minimumFractionDigits:0,maximumFractionDigits:0}).format(n)
}

const getCapacityPercentage = (plan) => {
    if (!plan.max_investors || plan.max_investors === 0) return 0
    const pct = Math.min(Math.round(((plan.current_investors||0)/plan.max_investors)*100), 100)
    return isNaN(pct) ? 0 : pct
}

const flash = computed(() => page.props.flash)
onMounted(() => {
    if (flash.value?.success) success(flash.value.success)
    else if (flash.value?.error) error(flash.value.error)
})
</script>

<style scoped>
/* ══ Section ══ */
.ipg { padding-bottom: 8px; }

/* ══ Header ══ */
.ipg-head {
    display: flex; align-items: center; justify-content: space-between;
    gap: 12px; flex-wrap: wrap; margin-bottom: 22px;
}
.ipg-head-l { display: flex; flex-direction: column; gap: 6px; }
.ipg-kicker {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 5px 12px; border-radius: 100px; border: 1px solid;
    font-size: 9px; font-weight: 800; letter-spacing: 0.12em; text-transform: uppercase; width: fit-content;
}
.ipg-kicker-dot {
    display: inline-block; width: 6px; height: 6px; border-radius: 50%;
    animation: ipgBlink 2s ease-in-out infinite;
}
.ipg-head-title { font-size: clamp(1rem,2.5vw,1.25rem); font-weight: 800; color: #fff; letter-spacing: -0.02em; margin: 0; }

.ipg-count {
    display: flex; flex-direction: column; align-items: center;
    padding: 8px 18px; border-radius: 14px; border: 1px solid;
    background: rgba(255,255,255,0.02); text-align: center; flex-shrink: 0;
}
.ipg-count-n { font-size: 1.5rem; font-weight: 900; letter-spacing: -0.04em; line-height: 1; }
.ipg-count-l { font-size: 9px; font-weight: 700; color: rgba(148,163,184,0.5); text-transform: uppercase; letter-spacing: 0.08em; margin-top: 2px; }

/* ══ Grid ══ */
.ipg-grid { display: grid; grid-template-columns: 1fr; gap: 16px; }
@media(min-width:640px)  { .ipg-grid { grid-template-columns: repeat(2,1fr); gap: 18px; } }
@media(min-width:1100px) { .ipg-grid { grid-template-columns: repeat(3,1fr); } }

/* ══ Card base ══ */
.ipc {
    position: relative;
    background: linear-gradient(145deg, rgba(12,5,26,0.98) 0%, rgba(20,8,40,0.96) 100%);
    border: 1px solid rgba(167,93,237,0.12);
    border-radius: 20px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: transform 0.3s cubic-bezier(0.34,1.56,0.64,1), box-shadow 0.3s ease, border-color 0.3s ease;
    cursor: default;
}
.ipc:hover {
    transform: translateY(-6px);
    border-color: rgba(167,93,237,0.3);
    box-shadow: 0 22px 52px rgba(0,0,0,0.55), 0 0 30px rgba(167,93,237,0.12);
}
.ipc:focus-visible { outline: 2px solid rgba(167,93,237,0.5); outline-offset: 2px; }

.ipc--featured { border-color: rgba(245,158,11,0.22); }
.ipc--featured:hover { border-color: rgba(245,158,11,0.4); box-shadow: 0 22px 52px rgba(0,0,0,0.5), 0 0 32px rgba(245,158,11,0.16); }
.ipc--soon { opacity: 0.72; }
.ipc--highlighted { animation: ipcHighlight 2.2s ease-in-out; }

/* Left stripe */
.ipc-stripe {
    position: absolute; left: 0; top: 0; bottom: 0; width: 4px; z-index: 3; flex-shrink: 0;
}

/* Corner glow */
.ipc-corner-glow {
    position: absolute; inset: 0; pointer-events: none; z-index: 0;
}

/* Top shimmer line */
.ipc-shimline {
    position: absolute; top: 0; left: 15%; right: 15%; height: 1px; z-index: 2;
}

/* Badges */
.ipc-badges {
    position: absolute; top: 12px; right: 12px;
    display: flex; flex-direction: column; align-items: flex-end; gap: 5px; z-index: 10;
}
.ipc-badge {
    display: inline-flex; align-items: center; gap: 4px;
    padding: 3px 9px; border-radius: 7px;
    font-size: 9px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase;
}
.ipc-badge--gold { background: linear-gradient(135deg,#d97706,#f59e0b); color: #1a0a00; }
.ipc-badge--soon { background: rgba(71,85,105,0.8); color: #94a3b8; border: 1px solid rgba(148,163,184,0.2); }

/* Card body — extra left padding to clear the stripe */
.ipc-body {
    padding: 16px 18px 20px 22px;
    display: flex; flex-direction: column; gap: 14px; flex: 1;
    position: relative; z-index: 1;
}
@media(min-width:640px) { .ipc-body { padding: 18px 20px 22px 24px; } }

/* Rate hero box */
.ipc-rate-hero {
    display: flex; align-items: center; justify-content: space-between; gap: 10px;
    padding: 12px 14px; border-radius: 13px; border: 1px solid;
}
.ipc-rate-left { display: flex; flex-direction: column; gap: 2px; }
.ipc-rate-type {
    font-size: 10px; font-weight: 700; color: rgba(148,163,184,0.55);
    text-transform: uppercase; letter-spacing: 0.1em;
}
.ipc-rate {
    font-size: clamp(2.6rem,5.5vw,3.5rem);
    font-weight: 900; letter-spacing: -0.05em; line-height: 1;
}
.ipc-rate-icon {
    width: 44px; height: 44px; border-radius: 12px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
    transition: transform 0.25s ease;
}
.ipc:hover .ipc-rate-icon { transform: scale(1.1) rotate(-5deg); }

/* Plan name & desc */
.ipc-name {
    font-size: clamp(1rem,2.5vw,1.15rem); font-weight: 800;
    color: #fff; letter-spacing: -0.02em; margin: 0; line-height: 1.2;
}
.ipc-desc {
    font-size: 12px; color: rgba(148,163,184,0.62);
    font-weight: 500; line-height: 1.5;
    display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;
    margin: 0;
}

/* Feature chips */
.ipc-features { display: flex; flex-wrap: wrap; gap: 5px; }
.ipc-chip {
    padding: 3px 9px; border-radius: 6px; border: 1px solid;
    font-size: 10px; font-weight: 700; letter-spacing: 0.02em;
}

/* Divider */
.ipc-divider { height: 1px; width: 100%; }

/* Details */
.ipc-details { display: flex; flex-direction: column; gap: 8px; }
.ipc-detail { display: flex; align-items: center; gap: 9px; }
.ipc-detail-ic {
    width: 24px; height: 24px; border-radius: 7px;
    display: flex; align-items: center; justify-content: center; flex-shrink: 0;
}
.ipc-detail-k {
    font-size: 10px; font-weight: 700; color: rgba(148,163,184,0.55);
    text-transform: uppercase; letter-spacing: 0.08em; flex: 1;
}
.ipc-detail-v { font-size: 12px; font-weight: 700; color: #e2e8f0; text-align: right; }

/* Capacity */
.ipc-cap { display: flex; flex-direction: column; gap: 6px; }
.ipc-cap-head { display: flex; justify-content: space-between; align-items: center; }
.ipc-cap-lbl { font-size: 10px; font-weight: 700; color: rgba(148,163,184,0.55); text-transform: uppercase; letter-spacing: 0.08em; }
.ipc-cap-pct { font-size: 12px; font-weight: 800; }
.ipc-cap-track {
    height: 6px; border-radius: 100px;
    background: rgba(255,255,255,0.06); overflow: hidden;
}
.ipc-cap-fill {
    position: relative; height: 100%; border-radius: 100px;
    transition: width 1.2s cubic-bezier(0.4,0,0.2,1); min-width: 4px; overflow: hidden;
}
.ipc-cap-shine {
    position: absolute; inset: 0;
    background: linear-gradient(90deg,transparent,rgba(255,255,255,0.28),transparent);
    animation: ipcShine 2.2s ease-in-out infinite;
}

/* ══ CTA Button ══ */
.ipc-btn {
    position: relative; overflow: hidden;
    width: 100%; padding: 13px 20px; border-radius: 13px;
    font-size: 14px; font-weight: 800; letter-spacing: 0.01em;
    border: none; cursor: pointer; outline: none;
    transition: transform 0.22s cubic-bezier(0.34,1.56,0.64,1), filter 0.2s ease, box-shadow 0.2s ease;
    margin-top: auto;
}
.ipc-btn:focus-visible { outline: 2px solid rgba(167,93,237,0.55); outline-offset: 2px; }
.ipc-btn:not(:disabled):hover { transform: translateY(-2px); filter: brightness(1.08); }
.ipc-btn:not(:disabled):active { transform: scale(0.97); }
.ipc-btn:disabled { cursor: not-allowed; opacity: 0.55; }

/* Shimmer slide on hover */
.ipc-btn-shimmer {
    position: absolute; inset: 0; pointer-events: none;
    background: linear-gradient(105deg, transparent 38%, rgba(255,255,255,0.2) 50%, transparent 62%);
    transform: translateX(-120%);
    transition: transform 0.55s ease;
}
.ipc-btn:not(:disabled):hover .ipc-btn-shimmer { transform: translateX(120%); }

.ipc-btn-inner {
    display: flex; align-items: center; justify-content: center; gap: 8px;
    position: relative; z-index: 1;
}

.ipc-btn--active   { color: #fff; }
.ipc-btn--featured {
    background: linear-gradient(135deg,#d97706,#f59e0b,#fcd34d);
    color: #1a0a00;
    box-shadow: 0 6px 20px rgba(245,158,11,0.4);
}
.ipc-btn--soon { background: rgba(71,85,105,0.5); color: #64748b; border: 1px solid rgba(71,85,105,0.35); }

/* ══ Keyframes ══ */
@keyframes ipcFadeUp {
    from { opacity:0; transform:translateY(24px); }
    to   { opacity:1; transform:none; }
}
@keyframes ipcGlow {
    0%,100% { box-shadow: 0 4px 20px rgba(0,0,0,0.3); }
    50%      { box-shadow: 0 4px 20px rgba(0,0,0,0.3), 0 0 28px rgba(167,93,237,0.1); }
}
.ipc:not(.ipc--soon):not(.ipc--featured) { animation: ipcFadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both, ipcGlow 4s 0.6s ease-in-out infinite; }
.ipc--featured { animation: ipcFadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both; }
.ipc--soon     { animation: ipcFadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both; }

@keyframes ipcShine { 0%{transform:translateX(-160%)} 100%{transform:translateX(160%)} }
@keyframes ipgBlink { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.4;transform:scale(0.7)} }
@keyframes ipcHighlight {
    0%,100% { box-shadow:none; }
    40%      { box-shadow:0 0 0 4px rgba(167,93,237,0.4),0 0 32px rgba(167,93,237,0.2); }
}

/* ══ Reduced motion ══ */
@media(prefers-reduced-motion:reduce) {
    .ipc, .ipc--featured, .ipc--soon, .ipc:not(.ipc--soon):not(.ipc--featured) { transition:none; animation:none; opacity:1; transform:none; }
    .ipc-cap-fill { transition:none; }
    .ipc-cap-shine, .ipg-kicker-dot { animation:none; }
    .ipc-btn:hover { transform:none; }
    .ipc-btn-shimmer { display:none; }
    .ipc-rate-icon { transition:none; }
}

/* ══ Mobile ══ */
@media(max-width:480px) {
    .ipc-body { padding: 14px 16px 18px 20px; }
    .ipg-head { margin-bottom: 16px; }
}
</style>
